import * as Api from '@ViewModels';
import * as React from 'react';
import { useStreamingResponseMutation } from '../../../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../../../queries/AI/useAIAssistantCreateConversationMutation';
import { getConversationChatMessageQueryKey } from '../../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { useUserTranscriptsQuery } from '../../../../../queries/Lead/useUserTranscriptsQuery';
import { reactQueryClient } from '../../../../../web/ReactQueryProvider';
import { formatBulkTranscripts } from '../utils/formatBulkTranscripts';

interface UseBulkTranscriptAnalysisProps {
	userId: string;
	startDate: string;
	endDate: string;
	source?: string;
}

const SALES_DEMO_CALL_COACHING_AGENT = 'SalesDemoCallCoachingAgent';

export const useBulkTranscriptAnalysis = ({ userId, startDate, endDate, source }: UseBulkTranscriptAnalysisProps) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useBulkTranscriptAnalysis');

	const [isAnalysisModalOpen, setIsAnalysisModalOpen] = React.useState(false);
	const [hasFetchedFeedback, setHasFetchedFeedback] = React.useState<boolean>(false);
	const [combinedTranscriptionText, setCombinedTranscriptionText] = React.useState<string>('');
	const [isNewAnalysisSession, setIsNewAnalysisSession] = React.useState<boolean>(true);
	const [shouldFetchTranscripts, setShouldFetchTranscripts] = React.useState<boolean>(false);
	// Ref to track if we've started conversation creation to prevent duplicate API calls
	const hasStartedConversationRef = React.useRef<boolean>(false);

	// Query for bulk transcripts - only enabled when analysis is triggered
	const transcriptsQuery = useUserTranscriptsQuery({
		userId,
		startDate,
		endDate,
		enabled: shouldFetchTranscripts,
		staleTime: 1000 * 60 * 60 * 1, // 1 hour
		meta: {
			toastErrorMessage: 'Failed to load transcripts for analysis.',
			errorLogging: {
				category: 'useBulkTranscriptAnalysis',
				action: 'useUserTranscriptsQuery',
			},
		},
	});

	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: (error: any) => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiAssistantCreateConversationMutation', error);
		},
		onSuccess: (conversation: any) => {
			const transcriptCount = transcriptsQuery.data?.transcriptCount || 0;
			const chatMessages: {
				role: Api.AIChatRole;
				text: string;
				id: string | null;
			}[] = [
				{
					role: Api.AIChatRole.User,
					text: `Multiple Demo Recordings (${transcriptCount} demos):\n${combinedTranscriptionText}`,
					id: null,
				},
			];

			aiPromptMutation.mutate({
				assistantName: SALES_DEMO_CALL_COACHING_AGENT,
				conversationId: conversation.id,
				chatRequest: {
					chatMessages,
				},
			});
		},
	});

	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({
			assistantName,
			conversationId,
			chatRequest,
		}: {
			assistantName: string;
			conversationId: string;
			chatRequest: Api.AIMessageRequest;
		}) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${conversationId}/stream`,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event: any, params) => {
			const data = JSON.parse(event.data);
			if (event.event !== 'Response') {
				return;
			}

			const messageText = data?.message?.text;

			if (messageText) {
				reactQueryClient.setQueryData(
					getConversationChatMessageQueryKey({
						id: params.conversationId,
					}),
					(messages: Api.AIChatMessage[]) => {
						if (messages?.length > 0 && messages[messages.length - 1].role === Api.AIChatRole.Assistant) {
							const lastAssistantMessage = messages.pop();
							let updatedText = lastAssistantMessage.text;

							updatedText = data.isDelta === false ? messageText : updatedText + messageText;

							return [
								...messages,
								{
									...lastAssistantMessage,
									text: updatedText,
								},
							];
						} else {
							return [
								...(messages || []),
								{
									id: data.message.id,
									role: Api.AIChatRole.Assistant,
									text: messageText,
									creationDate: new Date().toISOString(),
								},
							];
						}
					}
				);
			}
		},
		onError: (error: any) => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiPromptMutation', error);
		},
		onSuccess: () => {
			setHasFetchedFeedback(true);
		},
	});

	// When transcripts are loaded, format and create conversation
	React.useEffect(() => {
		if (
			transcriptsQuery.isSuccess &&
			transcriptsQuery.data &&
			shouldFetchTranscripts &&
			!hasStartedConversationRef.current
		) {
			if (transcriptsQuery.data.transcripts.length === 0) {
				errorMessages.push({
					messages: ['No transcripts found for this date range.'],
					title: 'No Transcripts',
				});
				setShouldFetchTranscripts(false);
				return;
			}

			const formattedTranscripts = formatBulkTranscripts(transcriptsQuery.data.transcripts);
			setCombinedTranscriptionText(formattedTranscripts);

			// Open modal now that transcripts are loaded
			setIsAnalysisModalOpen(true);

			// Mark that we've started the conversation
			hasStartedConversationRef.current = true;

			// Create conversation and start analysis
			aiAssistantCreateConversationMutation.mutate({
				assistantName: SALES_DEMO_CALL_COACHING_AGENT,
				source,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [transcriptsQuery.isSuccess, transcriptsQuery.data, shouldFetchTranscripts]);

	// Handle transcript loading errors
	React.useEffect(() => {
		if (transcriptsQuery.isError) {
			setIsAnalysisModalOpen(false);
			setShouldFetchTranscripts(false);
		}
	}, [transcriptsQuery.isError]);

	// Start analysis for bulk transcripts
	const startBulkAnalysis = async () => {
		aiPromptMutation.reset();
		setHasFetchedFeedback(false);
		setCombinedTranscriptionText('');
		setIsNewAnalysisSession(true);
		setShouldFetchTranscripts(true);
		hasStartedConversationRef.current = false;
	};

	const closeAnalysis = () => {
		aiPromptMutation.reset();
		setIsAnalysisModalOpen(false);
		setHasFetchedFeedback(false);
		setCombinedTranscriptionText('');
		setIsNewAnalysisSession(true);
		setShouldFetchTranscripts(false);
		hasStartedConversationRef.current = false;
	};

	// Reset tracking when starting a new AI session (but preserve conversation history for follow-ups)
	React.useEffect(() => {
		if (!hasFetchedFeedback && isNewAnalysisSession) {
			setIsNewAnalysisSession(false);
		}
	}, [hasFetchedFeedback, isNewAnalysisSession]);

	const sendFollowUp = (question: string) => {
		if (!question.trim()) return;

		reactQueryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: aiAssistantCreateConversationMutation.data.id,
			}),
			(messages: Api.AIChatMessage[]) => {
				return [...messages, { role: Api.AIChatRole.User, text: question }];
			}
		);

		// Reset AI response for new streaming response but keep conversation history
		setHasFetchedFeedback(false);

		aiPromptMutation.mutate({
			assistantName: SALES_DEMO_CALL_COACHING_AGENT,
			conversationId: aiAssistantCreateConversationMutation.data.id,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: question,
						id: null,
					},
				],
			},
		});
	};

	return {
		isAnalysisModalOpen,
		startBulkAnalysis,
		closeAnalysis,
		isLoading:
			transcriptsQuery.isFetching || aiAssistantCreateConversationMutation.isPending || aiPromptMutation.isLoading,
		isLoadingTranscripts: transcriptsQuery.isFetching,
		isCreatingConversation: aiAssistantCreateConversationMutation.isPending,
		sendFollowUp,
		conversationId: aiAssistantCreateConversationMutation.data?.id,
		aiPromptMutation,
	};
};
